import React from 'react';

/**
 * Hooks
 */
import { useTranslation } from 'react-i18next';
import useUserInfo from '../../hooks/User/useUserInfo';

/**
 * MUI Components
 */
import {
    Avatar,
    Box,
    Card,
    CardContent,
    CircularProgress,
    Alert,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Typography,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import BadgeIcon from '@mui/icons-material/Badge';
import TagIcon from '@mui/icons-material/Tag';
import WorkIcon from '@mui/icons-material/Work';

const UserInfoComponent = ({ token }) => {
    const { t } = useTranslation();
    const { userInfo, loading, error } = useUserInfo(token);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error" sx={{ mb: 2 }}>{error.message}</Alert>;
    }

    if (!userInfo) return null;

    const initials = userInfo.name
        ? userInfo.name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)
        : userInfo.username?.charAt(0).toUpperCase() || '?';

    const infoItems = [
        { icon: <PersonIcon />, label: t('username'), value: userInfo.username },
        { icon: <BadgeIcon />, label: t('Name'), value: userInfo.name },
        { icon: <EmailIcon />, label: t('Email'), value: userInfo.email },
    ];

    if (userInfo.staff) {
        infoItems.push(
            { icon: <WorkIcon />, label: t('Employee'), value: userInfo.staff.full_name },
        );
    }

    return (
        <Card variant="outlined">
            <CardContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
                    <Avatar
                        sx={{
                            width: 72,
                            height: 72,
                            bgcolor: 'primary.main',
                            fontSize: '1.5rem',
                            mb: 1,
                        }}
                    >
                        {initials}
                    </Avatar>
                    <Typography variant="h6">{userInfo.name || userInfo.username}</Typography>
                    {userInfo.staff && (
                        <Typography variant="body2" color="text.secondary">
                            {userInfo.staff.full_name}
                        </Typography>
                    )}
                </Box>

                <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TagIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                        <Box>
                            <Typography variant="body1">{userInfo.id}</Typography>
                            <Typography variant="caption" color="text.secondary">{t('User ID')}</Typography>
                        </Box>
                    </Box>
                    {userInfo.staff && (
                        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TagIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                            <Box>
                                <Typography variant="body1">{userInfo.staff.id}</Typography>
                                <Typography variant="caption" color="text.secondary">{t('Staff ID')}</Typography>
                            </Box>
                        </Box>
                    )}
                </Box>

                <List disablePadding>
                    {infoItems.map((item, idx) => (
                        <ListItem key={idx} disableGutters sx={{ py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 40, color: 'text.secondary' }}>
                                {item.icon}
                            </ListItemIcon>
                            <ListItemText
                                primary={item.value}
                                secondary={item.label}
                                primaryTypographyProps={{ variant: 'body1' }}
                                secondaryTypographyProps={{ variant: 'caption' }}
                            />
                        </ListItem>
                    ))}
                </List>
            </CardContent>
        </Card>
    );
};

export default UserInfoComponent;
